/**
 * Getting an authorization code for a CALENDAR.
 *
 * THE FLOW ITSELF NOW LIVES IN `shared/oauth/popupAuthCode.ts`, and its header
 * carries the three defects that produced it: Google never asked for offline
 * access, there was no `state`, there was no PKCE. All three came from the
 * browser building the authorize URL, and all three were fixed by asking the
 * API for it instead.
 *
 * WHAT STAYS HERE IS WHAT IS ABOUT CALENDARS: which endpoint mints the URL, and
 * which path each provider redirects back to.
 */

import { beginCalendarAuthorization } from '@biasafe.queries/calendar-provider-authorize/calendar-provider-authorize';
import {
  isCallbackMessage,
  popupAuthCode,
  type AuthCode,
  type CallbackMessage,
} from '@/shared/oauth/popupAuthCode';

export type { AuthCode };
export type CalendarProvider = 'GOOGLE' | 'OUTLOOK';

/** The stamp the calendar callback route puts on its message. */
export const CALENDAR_CALLBACK_SOURCE = 'biasafe-calendar-callback';

/**
 * Where each provider sends the popup back.
 *
 * SEPARATE PATHS PER PROVIDER, even though one component serves both. Each URI
 * has to be registered with the provider, and registering one path for two
 * providers would mean a change to either registration could break the other.
 */
export const CALENDAR_CALLBACK_PATHS: Record<CalendarProvider, string> = {
  GOOGLE: '/auth/google/calendar-callback',
  OUTLOOK: '/auth/microsoft/calendar-callback',
};

/** Kept for the route table, which registered this name before both existed. */
export const MICROSOFT_CALENDAR_CALLBACK_PATH = CALENDAR_CALLBACK_PATHS.OUTLOOK;

export type CalendarCallbackMessage = CallbackMessage;

export function isCalendarCallbackMessage(
  data: unknown,
): data is CalendarCallbackMessage {
  return isCallbackMessage(CALENDAR_CALLBACK_SOURCE, data);
}

export async function requestCalendarCode(
  provider: CalendarProvider,
): Promise<AuthCode> {
  return popupAuthCode({
    source: CALENDAR_CALLBACK_SOURCE,
    callbackPath: CALENDAR_CALLBACK_PATHS[provider],
    windowName: 'biasafe-calendar-connect',
    startFailure: 'Could not start the calendar connection.',
    begin: (redirectUri) =>
      beginCalendarAuthorization(provider === 'GOOGLE' ? 'google' : 'microsoft', {
        redirect_uri: redirectUri,
      }),
  });
}
